import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { siteConfig, fullAddress } from "../config/site";
import { useLanguage } from "../i18n/LanguageContext";
import { images } from "../config/images";

type Props = { title: string; description: string; withStructuredData?: boolean };

const setMeta = (selector: string, attrs: Record<string, string>) => {
  let el = document.head.querySelector<HTMLElement>(selector);
  if (!el) {
    el = document.createElement(selector.startsWith("link") ? "link" : "meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
};

export const SEOHead = ({ title, description, withStructuredData = false }: Props) => {
  const { pathname } = useLocation();
  const { lang } = useLanguage();

  useEffect(() => {
    const origin = siteConfig.siteUrl || window.location.origin;
    const url = `${origin}${pathname}`;
    document.title = title;
    setMeta('meta[name="description"]', { name: "description", content: description });
    setMeta('link[rel="canonical"]', { rel: "canonical", href: url });
    setMeta('meta[property="og:title"]', { property: "og:title", content: title });
    setMeta('meta[property="og:description"]', { property: "og:description", content: description });
    setMeta('meta[property="og:url"]', { property: "og:url", content: url });
    setMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    setMeta('meta[property="og:site_name"]', { property: "og:site_name", content: siteConfig.name });
    setMeta('meta[property="og:locale"]', { property: "og:locale", content: lang === "de" ? "de_DE" : "en_GB" });
    setMeta('meta[property="og:image"]', { property: "og:image", content: images.hero });
    setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });

    const existing = document.getElementById("ld-json");
    if (existing) existing.remove();
    if (withStructuredData) {
      // Nur bestätigte Angaben in den strukturierten Daten
      const data = {
        "@context": "https://schema.org",
        "@type": "LodgingBusiness",
        name: siteConfig.name,
        url: origin,
        image: images.hero,
        telephone: siteConfig.contacts.map((c) => c.phoneHref),
        email: siteConfig.contacts[0].email,
        address: {
          "@type": "PostalAddress",
          streetAddress: siteConfig.address.street,
          postalCode: siteConfig.address.zip,
          addressLocality: siteConfig.address.city,
          addressCountry: siteConfig.address.countryCode,
        },
        numberOfRooms: siteConfig.stats.rooms,
        priceRange: `ab ${siteConfig.price.perBedPerNight} ${siteConfig.price.currency} pro Bett/Nacht`,
        amenityFeature: [
          { "@type": "LocationFeatureSpecification", name: "Kostenloses WLAN", value: true },
          { "@type": "LocationFeatureSpecification", name: "Gemeinschaftsküche", value: true },
          { "@type": "LocationFeatureSpecification", name: "Gemeinschaftliche Badezimmer", value: true },
        ],
        description: description,
        keywords: "Pension Königsbrunn, Monteurzimmer Königsbrunn, Monteurzimmer Augsburg, Unterkunft Königsbrunn, Unterkunft für Monteure",
      };
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "ld-json";
      script.text = JSON.stringify(data);
      document.head.appendChild(script);
    }
  }, [title, description, pathname, lang, withStructuredData]);

  return null;
};

export const addressLine = fullAddress;
